import React, { useEffect, useCallback, useState, useMemo } from 'react';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn } from '../components/data/DataGrid';
import FinancialRecordFilterPanel from '../components/data/FinancialRecordFilterPanel';
import FinancialDataChatbot from '../components/chat/FinancialDataChatbot';
import financialRecordKPIService from '../services/financialRecordKPIService';
import { UserService } from '../services/userService';
import { FinancialRecord, FinancialRecordFilter, KPI } from '../types/data/financialRecord';
import { User } from '../types/user';
import useAuthStore from '../store/authStore';
import './FinancialDataPage.css';

// Component to render KPI details in expanded rows
const KPIDetailsComponent: React.FC<{ record: FinancialRecord; appliedFilter?: FinancialRecordFilter }> = ({ record, appliedFilter }) => {
  // Filter KPIs based on applied categories filter
  const filteredKpis = record.kpis?.filter((kpi: KPI) => {
    if (appliedFilter?.kpiCategories && appliedFilter.kpiCategories.length > 0) {
      return kpi.category && appliedFilter.kpiCategories.includes(kpi.category);
    }
    return true; // No filter applied, show all KPIs
  }) || [];

  const hasKpis = filteredKpis.length > 0;
  const hasAdditionalData = record.additionalData && record.additionalData.length > 0;

  if (!hasKpis && !hasAdditionalData) {
    return (
      <div className="kpi-empty-state">
        <div className="kpi-empty-icon">📊</div>
        <p>No KPIs or additional data available for this record</p>
      </div>
    );
  }

  return (
    <div className="kpi-details">
      {/* KPI Section */}
      {hasKpis && (
        <>
          <div className="kpi-details-header">
            <h4 className="kpi-details-title">📊 KPI Details</h4>
            <span className="kpi-count-badge">{filteredKpis.length} KPIs</span>
          </div>
          
          <div className="kpi-grid">
            {filteredKpis.map((kpi: KPI, index: number) => (
              <div key={`${kpi.code}-${index}`} className="kpi-item">
                <div className="kpi-header">
                  <span className="kpi-code">{kpi.code}</span>
                  {kpi.category && (
                    <span className="kpi-category">{kpi.category}</span>
                  )}
                </div>
                
                <div className="kpi-name">{kpi.name || 'Unnamed KPI'}</div>
                
                <div className="kpi-value-container">
                  <div className="kpi-value">
                    <span>
                      {typeof kpi.value === 'number' 
                        ? kpi.value.toLocaleString() 
                        : kpi.value || 'N/A'}
                    </span>
                    {kpi.unit && <span className="kpi-unit">{kpi.unit}</span>}
                  </div>
                  
                  <div className="kpi-description">
                    {kpi.description || 'No description available'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Additional Data Section */}
      {hasAdditionalData && (
        <>
          <div className="kpi-details-header additional-data-header">
            <h4 className="kpi-details-title">📝 Additional Data</h4>
            <span className="kpi-count-badge">{record.additionalData?.length} Fields</span>
          </div>
          
          <div className="additional-data-grid">
            {record.additionalData?.map((item, index) => (
              <div key={`additional-${index}`} className="additional-data-item">
                <div className="additional-data-field">{item.field}</div>
                <div className="additional-data-value">{item.value}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FinancialDataPage: React.FC = () => {
  const { user } = useAuthStore();
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FinancialRecordFilter>({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingRecords, setDeletingRecords] = useState<string[]>([]);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string | number>>(new Set());
  const [userMap, setUserMap] = useState<Map<string, User>>(new Map());
  const [filterUserIds, setFilterUserIds] = useState<string[]>([]);
  const [showChatbot, setShowChatbot] = useState(false);

  const [directFilterOptions, setDirectFilterOptions] = useState<{
    brands: string[];
    dealers: string[];
    years: number[];
    months: number[];
    kpiCategories: string[];
  } | null>(null);
  const pageSize = 20;

  // Handle record deletion
  const handleDeleteRecord = async (id: string) => {
    const record = records.find(r => r.id === id);
    const recordDescription = record ? `${record.brand} - ${record.dealer} (${record.year}/${record.month})` : `ID ${id}`;
    
    if (window.confirm(`Are you sure you want to delete this financial record?\n\n${recordDescription}\n\nThis action cannot be undone.`)) {
      try {
        setDeletingRecords(prev => [...prev, id]);
        
                const success = await financialRecordKPIService.deleteFinancialRecord(id);
        
        if (success) {
                    // Remove the record from local state immediately for better UX
          setRecords(prev => prev.filter(r => r.id !== id));
          setTotalCount(prev => prev - 1);
          
          // Also remove from expanded rows if it was expanded
          setExpandedRowIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
          
          // Show success message
          alert(`Successfully deleted record: ${recordDescription}`);
        } else {
          console.error(`DEBUG FINANCIAL DATA PAGE: Failed to delete record ${id}`);
          alert('Failed to delete the record. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('An error occurred while deleting the record. Please try again.');
      } finally {
        setDeletingRecords(prev => prev.filter(recordId => recordId !== id));
      }
    }
  };

  // Handle row expansion
  const handleRowExpand = (rowId: string | number) => {
    setExpandedRowIds(prev => {
      const newSet = new Set(prev);
      newSet.add(rowId);
      return newSet;
    });
  };

  // Handle row collapse
  const handleRowCollapse = (rowId: string | number) => {
    setExpandedRowIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(rowId);
      return newSet;
    });
  };

  // Define columns for the data grid
  const columns: GridColumn[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'brand', headerName: 'Brand', width: 120 },
    { field: 'dealer', headerName: 'Dealer', width: 150 },
    { field: 'location_id', headerName: 'Location ID', width: 100, type: 'number' },
    { field: 'year', headerName: 'Year', width: 80, type: 'number' },
    { field: 'month', headerName: 'Month', width: 80, type: 'number' },
    { 
      field: 'groups', 
      headerName: 'Groups', 
      width: 150,
      renderCell: (row: any) => {
        const groups = row.groups;
        if (!groups || groups.length === 0) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>No groups</span>;
        }
        return <span title={groups.join(', ')}>{groups.join(', ')}</span>;
      }
    },
    { field: 'kpiCount', headerName: 'KPIs', width: 80, type: 'number' },
    { 
      field: 'additionalDataCount', 
      headerName: 'Add. Data', 
      width: 90, 
      type: 'number',
      renderCell: (row: any) => {
        const count = row.additionalDataCount || 0;
        if (count === 0) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>None</span>;
        }
        return <span title={`${count} additional data fields`}>{count}</span>;
      }
    },
    // Show owner column only for admins and group admins
    ...(user?.role === 'admin' || user?.role === 'group_admin' ? [{
      field: 'ownerName',
      headerName: 'Owner',
      width: 120,
      renderCell: (row: any) => {
        if (!row.userId) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>System</span>;
        }
        const isCurrentUser = row.userId === user?.id;
        return (
          <span style={{ 
            color: isCurrentUser ? '#007bff' : '#6c757d',
            fontWeight: isCurrentUser ? 'bold' : 'normal'
          }}>
            {isCurrentUser ? 'You' : row.ownerName || row.userId}
          </span>
        );
      }
    }] : []),
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 120,
      type: 'date'
    },
    { 
      field: 'updatedAt', 
      headerName: 'Updated', 
      width: 120,
      type: 'date'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (row: any) => {
        const recordId = String(row.id);
        const isDeleting = deletingRecords.includes(recordId);
        
        return (
          <button 
            className={`delete-record-btn ${isDeleting ? 'deleting' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteRecord(recordId);
            }}
            disabled={isDeleting}
            title={isDeleting ? 'Deleting...' : 'Delete this record'}
          >
            {isDeleting ? '⏳' : '🗑️'}
          </button>
        );
      }
    }
  ];

  // Fetch records from the KPI-based financial records service
  const fetchRecords = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
                  // Convert filter to the format expected by the KPI service
      // Only include filter properties that have values
      const kpiFilters: any = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (filter.brands && filter.brands.length > 0) kpiFilters.brands = filter.brands;
      if (filter.dealers && filter.dealers.length > 0) kpiFilters.dealers = filter.dealers;
      if (filter.years && filter.years.length > 0) kpiFilters.years = filter.years;
      if (filter.months && filter.months.length > 0) kpiFilters.months = filter.months;
      if (filter.kpiCodes && filter.kpiCodes.length > 0) kpiFilters.kpiCodes = filter.kpiCodes;
      if (filter.kpiCategories && filter.kpiCategories.length > 0) kpiFilters.kpiCategories = filter.kpiCategories;
      if (filter.owners && filter.owners.length > 0) kpiFilters.owners = filter.owners;
      
                  const response = await financialRecordKPIService.getFinancialRecords(kpiFilters);
      
                  setRecords(response.records || []);
      setTotalCount(response.totalCount || 0);
      setCurrentPage(response.page || 1);
    } catch (err) {
      console.error('Error fetching financial records:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch financial records');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users for name mapping (only for admins and group admins)
  const fetchUsers = async () => {
    if (user?.role === 'admin' || user?.role === 'group_admin') {
      try {
                // Get the owner IDs extracted from the records by the filter panel
        const extractedOwnerIds = (window as any).availableOwnerIds || [];
                // Use these IDs to fetch user details
        if (extractedOwnerIds.length > 0) {
          const users = await UserService.getUsersByIds(extractedOwnerIds);
                    // Create a map of user ID to user object
          const newUserMap = new Map<string, User>();
          users.forEach(u => {
            const userId = String(u.id);
            newUserMap.set(userId, u);
          });
          
          setUserMap(newUserMap);
          setFilterUserIds(extractedOwnerIds);
          
                            } else {
                    setUserMap(new Map());
          setFilterUserIds([]);
        }
      } catch (error) {
        console.error('Error fetching users for name mapping:', error);
        setUserMap(new Map());
        setFilterUserIds([]);
      }
    }
  };

  // Fetch users when records change, user role changes, or extracted owner IDs change
  useEffect(() => {
    fetchUsers();
  }, [records, user?.role, (window as any).availableOwnerIds]);

  // Transform records for the data grid
  const transformedRecords = useMemo(() => {
    return records.map(record => {
      // Get owner name from user map
      let ownerName = '';
      if (record.userId) {
        // Ensure consistent ID format between records and user map
        const normalizedId = String(parseInt(String(record.userId)));
        ownerName = userMap.get(normalizedId)?.name || `User ${normalizedId}`;
      }

      // Filter KPIs based on applied categories filter for accurate count
      const filteredKpis = record.kpis?.filter((kpi: KPI) => {
        if (filter.kpiCategories && filter.kpiCategories.length > 0) {
          return kpi.category && filter.kpiCategories.includes(kpi.category);
        }
        return true; // No filter applied, count all KPIs
      }) || [];

      return {
        ...record,
        createdAt: record.createdAt, // Keep original timestamp, formatting handled by valueFormatter
        updatedAt: record.updatedAt, // Keep original timestamp, formatting handled by valueFormatter
        kpiCount: filteredKpis.length,
        additionalDataCount: record.additionalData?.length || 0,
        ownerName,
        _fullRecord: record // Store the full record for expanded view
      };
    });
  }, [records, userMap, filter.kpiCategories]);

  // Render expanded row content
  const renderExpandedRow = (row: any) => {
    const fullRecord = row._fullRecord as FinancialRecord;
    return <KPIDetailsComponent record={fullRecord} appliedFilter={filter} />;
  };

  // Handle filter application
  const handleApplyFilter = useCallback((newFilter: FinancialRecordFilter) => {
                                setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filtering
    // Clear expanded rows when filter changes
    setExpandedRowIds(new Set());
  }, []);

  // Handle filter clearing
  const handleClearFilter = useCallback(() => {
        setFilter({});
    setCurrentPage(1); // Reset to first page when clearing filter
    // Clear expanded rows when filter is cleared
    setExpandedRowIds(new Set());
  }, []);

  // Handle page changes
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= Math.ceil(totalCount / pageSize)) {
      setCurrentPage(page);
      // Clear expanded rows when page changes
      setExpandedRowIds(new Set());
    }
  }, [totalCount, pageSize]);

  // Handle delete all records
  const handleDeleteAllRecords = async () => {
    // Check if any filters are applied
    const hasFilters = (
      (filter.brands && filter.brands.length > 0) ||
      (filter.dealers && filter.dealers.length > 0) ||
      (filter.years && filter.years.length > 0) ||
      (filter.months && filter.months.length > 0) ||
      (filter.kpiCategories && filter.kpiCategories.length > 0) ||
      (filter.owners && filter.owners.length > 0)
    );

    const confirmMessage = hasFilters 
      ? `Are you sure you want to delete the filtered financial records?\n\nThis will permanently delete ${totalCount} records that match your current filter criteria.\n\nThis action cannot be undone.`
      : `Are you sure you want to delete ALL financial records?\n\nThis will permanently delete ${totalCount} records.\n\nThis action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        setIsDeletingAll(true);
        
                // Use filtered deletion if any filters are applied, otherwise delete all
        const result = hasFilters 
          ? await financialRecordKPIService.deleteFilteredFinancialRecords({
              brands: filter.brands?.length ? filter.brands : undefined,
              dealers: filter.dealers?.length ? filter.dealers : undefined,
              years: filter.years?.length ? filter.years : undefined,
              months: filter.months?.length ? filter.months : undefined,
              kpiCategories: filter.kpiCategories?.length ? filter.kpiCategories : undefined,
              owners: filter.owners?.length ? filter.owners : undefined
            })
          : await financialRecordKPIService.deleteAllFinancialRecords();
        
                // Refresh the data to reflect changes
        await fetchRecords();
        
        // Show success message
        const deletionType = hasFilters ? 'filtered' : 'all';
        alert(`Successfully deleted ${result.deletedCount} ${deletionType} financial records`);
      } catch (error) {
        console.error('Error deleting records:', error);
        alert('An error occurred while deleting records. Please try again.');
      } finally {
        setIsDeletingAll(false);
      }
    }
  };

  // Fetch records when filter or page changes (this handles initial load too)
  useEffect(() => {
    fetchRecords();
  }, [filter, currentPage, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch filter options using optimized API endpoint
  const fetchFilterOptions = async () => {
    try {
      console.log('🚀 PERFORMANCE: Using optimized filter options API');
      
      // Use the new optimized filter options API
      const options = await financialRecordKPIService.getFilterOptions({
        brands: filter.brands,
        dealers: filter.dealers,
        years: filter.years,
        months: filter.months,
        kpiCategories: filter.kpiCategories,
        owners: filter.owners
      });
      
      // Store filter options directly
      setDirectFilterOptions({
        brands: options.brands || [],
        dealers: options.dealers || [],
        years: options.years || [],
        months: options.months || [],
        kpiCategories: options.kpiCategories || []
      });
      
      console.log('✅ PERFORMANCE: Filter options loaded efficiently', {
        brandsCount: options.brands?.length || 0,
        dealersCount: options.dealers?.length || 0,
        yearsCount: options.years?.length || 0,
        monthsCount: options.months?.length || 0,
        kpiCategoriesCount: options.kpiCategories?.length || 0
      });
      
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // Fallback to the old method if filter options endpoint fails
      await fetchAllRecordsForFiltersFallback();
    }
  };

  // Fallback function (kept for compatibility)
  const fetchAllRecordsForFiltersFallback = async () => {
    try {
            const response = await financialRecordKPIService.getFinancialRecords({
        page: 1,
        pageSize: 50000
      });
      
      if (response.records && response.records.length > 0) {
        // Extract filter options from all fetched records
        const allKpis = response.records.flatMap(r => r.kpis || []);
        const uniqueBrands = Array.from(new Set(response.records.map(r => r.brand).filter((b): b is string => Boolean(b)))).sort();
        const uniqueDealers = Array.from(new Set(response.records.map(r => r.dealer).filter((d): d is string => Boolean(d)))).sort();
        const uniqueYears = Array.from(new Set(response.records.map(r => r.year))).sort((a, b) => b - a);
        const uniqueMonths = Array.from(new Set(response.records.map(r => r.month))).sort((a, b) => a - b);
        const uniqueKpiCategories = Array.from(new Set(allKpis.map(kpi => kpi.category).filter((c): c is string => Boolean(c)))).sort();
        
        // Set direct filter options from fallback data
        setDirectFilterOptions({
          brands: uniqueBrands,
          dealers: uniqueDealers,
          years: uniqueYears,
          months: uniqueMonths,
          kpiCategories: uniqueKpiCategories
        });
        
                const uniqueOwnerIds = Array.from(new Set(
          response.records
            .map(record => record.userId)
            .filter((userId): userId is string => userId !== undefined && userId !== null)
            .map(id => String(id).replace('.0', '')) // Simple normalization
        ));
        
        setFilterUserIds(uniqueOwnerIds);
        (window as any).availableOwnerIds = uniqueOwnerIds;
        
        // Only fetch user details for admin/group_admin users
        if (uniqueOwnerIds.length > 0 && (user?.role === 'admin' || user?.role === 'group_admin')) {
                    try {
            const users = await UserService.getUsersByIds(uniqueOwnerIds);
            const newUserMap = new Map<string, User>();
            users.forEach(u => {
              newUserMap.set(String(u.id), u);
            });
            setUserMap(newUserMap);
          } catch (userError) {
            console.error('Error fetching user details in fallback:', userError);
          }
        } else if (user?.role !== 'admin' && user?.role !== 'group_admin') {
          // Regular user - no need to fetch user details
        }
      } else {
        // If no records found, set empty options
        setDirectFilterOptions({
          brands: [],
          dealers: [],
          years: [],
          months: [],
          kpiCategories: []
        });
      }
    } catch (error) {
      console.error('Error fetching records for filter options (fallback):', error);
      // Set empty options on error
      setDirectFilterOptions({
        brands: [],
        dealers: [],
        years: [],
        months: [],
        kpiCategories: []
      });
    }
  };

  // Fetch filter options on component mount and when filter changes
  useEffect(() => {
    if (user?.role) {
      fetchFilterOptions();
    }
  }, [user?.role, filter]);
      return (
    <div className="financial-data-page">
      <Sidebar />
      
      <div className="financial-data-content">


        {error && (
          <div className="error-message">
            Error: {error}
          </div>
        )}
        
        <div className="data-filter-section">
          <FinancialRecordFilterPanel
            initialFilter={filter}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilter}
            showOwnerFilter={user?.role === 'admin' || user?.role === 'group_admin'}
            records={records} // Only used for fallback, not for extracting filter options
            userMap={userMap}
            filterUserIds={filterUserIds}
            availableOptions={directFilterOptions ? directFilterOptions : undefined} // Pass filter options directly
          />
        </div>
        
        <div className="data-grid-section">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading financial records...</p>
            </div>
          ) : records.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <h3>No Financial Records Found</h3>
              <p>No records match the current filter criteria.</p>
            </div>
          ) : (
            <>
              <div className="data-grid-container">
                <DataGrid 
                  columns={columns}
                  rows={transformedRecords}
                  loading={isLoading}
                  height={600}
                  expandable={true}
                  renderExpandedRow={renderExpandedRow}
                  expandedRowIds={expandedRowIds}
                  onRowExpand={handleRowExpand}
                  onRowCollapse={handleRowCollapse}
                />
              </div>
              
              <div className="data-grid-actions">
                <button
                  className="delete-all-btn"
                  onClick={handleDeleteAllRecords}
                  disabled={isDeletingAll || records.length === 0}
                  title={
                    isDeletingAll 
                      ? 'Deleting records...' 
                      : (() => {
                          const hasFilters = (
                            (filter.brands && filter.brands.length > 0) ||
                            (filter.dealers && filter.dealers.length > 0) ||
                            (filter.years && filter.years.length > 0) ||
                            (filter.months && filter.months.length > 0) ||
                            (filter.kpiCategories && filter.kpiCategories.length > 0) ||
                            (filter.owners && filter.owners.length > 0)
                          );
                          return hasFilters ? 'Delete filtered records' : 'Delete all records';
                        })()
                  }
                >
                  {isDeletingAll 
                    ? '⏳ Deleting...' 
                    : (() => {
                        const hasFilters = (
                          (filter.brands && filter.brands.length > 0) ||
                          (filter.dealers && filter.dealers.length > 0) ||
                          (filter.years && filter.years.length > 0) ||
                          (filter.months && filter.months.length > 0) ||
                          (filter.kpiCategories && filter.kpiCategories.length > 0) ||
                          (filter.owners && filter.owners.length > 0)
                        );
                        return hasFilters ? '🗑️ Delete Filtered Records' : '🗑️ Delete All Records';
                      })()
                  }
                </button>
              </div>
              
              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="pagination-page">{currentPage} of {totalPages}</span>
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chatbot Toggle Button - Outside content container for proper fixed positioning */}
      <div className="chatbot-toggle-container">
        <button 
          className="chatbot-toggle-button"
          onClick={() => setShowChatbot(!showChatbot)}
          title={showChatbot ? 'Close AI Assistant' : 'Open AI Assistant'}
        >
          <span className="chatbot-toggle-icon">💬</span>
          <span className="chatbot-toggle-text">
            {showChatbot ? 'Close AI Assistant' : 'Ask AI About Data'}
          </span>
          <span className="chatbot-toggle-count">
            {totalCount} records
          </span>
        </button>
      </div>

      {/* AI Data Assistant Chatbot */}
      <FinancialDataChatbot
        currentFilter={{
          // SECURITY FIX: Never pass owners to the chatbot - let backend handle user access control
          brands: filter.brands,
          dealers: filter.dealers,
          years: filter.years,
          months: filter.months,
          kpiCodes: filter.kpiCodes,
          kpiCategories: filter.kpiCategories,
          // owners: filter.owners, // REMOVED: Backend will determine user access based on authentication
          limit: filter.limit
        }}
        currentDataCount={totalCount}
        isVisible={showChatbot}
        onClose={() => setShowChatbot(false)}
      />
    </div>
  );
};

export default FinancialDataPage; 